import React, { useEffect, useState } from 'react'

import { checkSupportedDevice } from '@lib/utils/system-check'
import {
  CAM_AND_MIC_ACCESS_VIDEOS,
  IMAGE_PATH,
  SYSTEM_CHECK_ERRORS,
  VALID_BROWSER_KEYS,
} from '@lib/constants/system-check'

import Modal from '@components/common/modal'
import Spinner from '@components/common/spinner'
import Router from 'next/router'
import {
  SUPPORT_EMAIL,
  SUPPORT_PHONE_NUMBER,
  WHATSAPP_SUPPORT_NUMBER,
} from '@common/constants'
import useSystemCheck from '@hooks/useSystemCheck'
import { LazyImage } from '@components/common/image'
import { useSessionContext } from '@contexts/session'
import { TRIAL_CLASS_JOIN_SCREEN_SEGMENT_ENUM } from '@common/enums'
import { getSegmentUserType, isEmptyObject } from '@lib/utils/common'
import { SystemCheckModalErrorType } from '@lib/types/system-check'
import useIsIndia from '@hooks/useIsIndia'
import { CheckCircle, Lock, X, XCircle } from 'react-feather'
import { Icon } from '@components/common/icons'
import { t, Trans } from '@lingui/macro'
import { i18n } from '@lingui/core'
import MsgTrans from '@components/common/i18n/message-trans'
import { sleep } from '@common/utils'

export const CAM_AND_MIC_ACCESS_STEPS = {
  firefox: [
    <Trans id="trialClass.systemCheck.fixErrors.firefox.1" key="1">
      Click the camera or microphone icon on the left side of browser address
      bar where codingal.com is written.
    </Trans>,
    <Trans id="trialClass.systemCheck.fixErrors.firefox.2" key="2">
      Remove the blocked status for both camera and microphone by clicking on
      the cross icon <X className="inline w-5" /> You need to refresh the page
      for changes to apply.
    </Trans>,
    <Trans id="trialClass.systemCheck.fixErrors.firefox.3" key="3">
      Run the system check again and when you see the camera access prompt,
      select the check box &ldquo;Remember this decision&rdquo; and click on
      allow.
    </Trans>,
  ],
  safari: [
    <Trans id="trialClass.systemCheck.fixErrors.safari.1" key="1">
      Take the Mouse pointer to the middle of the URL bar where the name of the
      website is written &ldquo;codingal.com&rdquo; and right click on it.
    </Trans>,
    <Trans id="trialClass.systemCheck.fixErrors.safari.2" key="2">
      Select the &ldquo;Settings for This Website…&rdquo; option. A dropdown
      window will open
    </Trans>,
    <Trans id="trialClass.systemCheck.fixErrors.safari.3" key="3">
      If the camera and microphone access are set on &ldquo;Deny&rdquo;, you can
      change it to &ldquo;Allow&rdquo;. Refresh the page after it done.
    </Trans>,
  ],
  chrome: [
    <Trans id="trialClass.systemCheck.fixErrors.chrome.1" key="1">
      Take your mouse pointer to the <Lock className="inline w-5" /> lock icon
      on the left side of browser address bar where codingal.com is written.
    </Trans>,
    <Trans id="trialClass.systemCheck.fixErrors.chrome.2" key="2">
      Click on it, a small window will open showing that connection is secure.
      Check if the Camera and Microphone access are allowed.
    </Trans>,
    <Trans id="trialClass.systemCheck.fixErrors.chrome.3" key="3">
      If either camera or microphone access is blocked, you can allow it from
      the drop-down. Refresh page after it’s done.
    </Trans>,
  ],
  microsoftedge: [
    <Trans id="trialClass.systemCheck.fixErrors.edge.1" key="1">
      Take your mouse pointer to the <Lock className="inline w-5" /> lock icon
      on the left side of browser address bar where codingal.com is written.
    </Trans>,
    <Trans id="trialClass.systemCheck.fixErrors.edge.2" key="2">
      Click on it, a small window will open showing that connection is secure.
      Check if the Camera and Microphone access are allowed.
    </Trans>,
    <Trans id="trialClass.systemCheck.fixErrors.edge.3" key="3">
      If either camera or microphone access is blocked, you can allow it from
      the drop-down. Refresh page after it’s done.
    </Trans>,
  ],
}

const {
  TRACK_COMPUTER_CHECK_SUCCESS,
  TRACK_COMPUTER_CHECK_ERROR,
  TRACK_DOWNLOAD_GOOGLE_CHROME_CTA_CLICK,
  TRACK_TEST_INTERNET_SPEED_CTA_CLICK,
} = TRIAL_CLASS_JOIN_SCREEN_SEGMENT_ENUM

type Props = {
  showSystemCheckModal: boolean
  setShowSystemCheckModal: React.Dispatch<React.SetStateAction<boolean>>
  setSystemCheckSuccess?: React.Dispatch<React.SetStateAction<boolean>>
  shouldCloseOnEsc?: boolean
  autoClose?: boolean
  classroomId?: number
  isResultStoredLocally?: boolean
  isTrialJoinPage?: boolean
  setSystemCheckErrors?: React.Dispatch<
    React.SetStateAction<SystemCheckModalErrorType[]>
  >
}

const SystemCheckModal = ({
  showSystemCheckModal,
  setShowSystemCheckModal,
  setSystemCheckSuccess,
  shouldCloseOnEsc = true,
  autoClose = false,
  classroomId,
  isResultStoredLocally = false,
  setSystemCheckErrors = null,
  isTrialJoinPage,
}: Props): JSX.Element => {
  const {
    id,
    student,
    name,
    phone,
    email,
    gender,
    hasLaptop,
    signupSource,
    isPaidUser,
    isCompetitionUser,
  } = useSessionContext()

  const segmentTrackingData = {
    userId: id,
    studentId: student?.id,
    name,
    phone,
    email,
    grade: student?.grade,
    gender,
    hasLaptop,
    signupSource,
    device: checkSupportedDevice().device,
    userType: getSegmentUserType(isPaidUser, isCompetitionUser),
  }

  const { isIndia } = useIsIndia()
  const {
    isChecking,
    passed,
    systemCheckResults,
    runSystemCheck,
  } = useSystemCheck(
    {
      checkBrowser: true,
      checkCookies: true,
      checkDevice: true,
      checkScreenSize: true,
      checkMicrophoneAndCamera: true,
    },
    {
      classroomId,
      isResultStoredLocally,
    }
  )

  const [errorList, setErrorList] = useState<SystemCheckModalErrorType[]>([])
  const [isDeviceSupported, setIsDeviceSupported] = useState(false)
  const [isCamOrMicCheckFailed, setIsCamOrMicCheckFailed] = useState(false)

  useEffect(() => {
    setIsDeviceSupported(checkSupportedDevice().passed)
  }, [])

  useEffect(() => {
    if (showSystemCheckModal) {
      setErrorList([])
      runSystemCheck()
    }
  }, [showSystemCheckModal])

  const handleSystemCheckErrors = () => {
    const currentErrorList: SystemCheckModalErrorType[] = []
    Object.entries(systemCheckResults).forEach(
      ([checkType, checkResult]) =>
        !checkResult.passed &&
        SYSTEM_CHECK_ERRORS[checkType] &&
        currentErrorList.push(SYSTEM_CHECK_ERRORS[checkType])
    )
    setErrorList(
      currentErrorList.length ? currentErrorList : [SYSTEM_CHECK_ERRORS.generic]
    )

    setSystemCheckErrors?.(currentErrorList)

    setIsCamOrMicCheckFailed(
      !systemCheckResults.camera?.passed ||
        !systemCheckResults.microphone?.passed
    )
  }

  const handleCheckComplete = () => {
    handleSystemCheckErrors()
    setSystemCheckSuccess && setSystemCheckSuccess(passed)

    if (isTrialJoinPage) {
      if (passed) {
        window.globalThis?.analytics?.track(
          TRACK_COMPUTER_CHECK_SUCCESS,
          segmentTrackingData
        )
        sleep(5000).then(() => {
          setShowSystemCheckModal(false)
        })
      } else {
        window.globalThis?.analytics?.track(
          TRACK_COMPUTER_CHECK_ERROR,
          segmentTrackingData
        )
      }
    }

    if (autoClose) setShowSystemCheckModal(false)
  }

  useEffect(() => {
    if (
      !isChecking &&
      !isEmptyObject(systemCheckResults) &&
      showSystemCheckModal
    ) {
      handleCheckComplete()
    }
  }, [isChecking, passed])

  const browserKey = systemCheckResults.browser?.name
    .toLowerCase()
    .replace(' ', '')

  return (
    <Modal
      isOpen={showSystemCheckModal}
      setIsOpen={setShowSystemCheckModal}
      width="sm:max-w-5xl"
      shouldCloseOnEsc={shouldCloseOnEsc}
    >
      <>
        {!autoClose && (
          <button
            className="absolute right-0 top-0 mr-5 mt-5"
            type="button"
            onClick={() => setShowSystemCheckModal(false)}
          >
            <X className="text-grey" />
          </button>
        )}
        {!isDeviceSupported ? (
          <div className="flex flex-col items-center justify-center my-6">
            <div className="flex items-center flex-col">
              <XCircle className="text-orange mx-2" size={40} />
              <h1 className="text-3xl text-center">
                <Trans id="trialClass.systemCheck.mobileDeviceNotSupported">
                  Mobile device not supported
                </Trans>
              </h1>
            </div>
            <p className="max-w-lg text-center m-4 text-lg">
              <Trans id="trialClass.systemCheck.mobileDeviceNotSupportedErrorMessage">
                Mobile device is not supported to attend classes on Codingal. To
                join your class, go to{' '}
                <span className="text-orange font-600 hover:underline">
                  www.codingal.com
                </span>
                , click on <span className="font-600">Login</span> at the top,
                and then click on <span className="font-600">Join class</span>{' '}
                in your dashboard.
              </Trans>
            </p>
          </div>
        ) : (
          <div className="flex flex-col">
            <div
              className="grid gap-6 overflow-auto thin-scroll-bar px-2 my-2"
              style={{ maxHeight: autoClose ? '70vh' : '80vh' }}
            >
              {isChecking && (
                <div className="flex flex-col items-center justify-center my-5">
                  <Spinner classes="h-10 w-10 text-orange" />
                  <span className="mt-5 text-2xl text-grey text-center">
                    <Trans id="trialClass.systemCheck.runningCheck">
                      Running system check
                    </Trans>
                  </span>
                </div>
              )}
              {!isChecking && (
                <div className="flex flex-col items-center justify-center mt-2">
                  {passed ? (
                    <CheckCircle className="text-green-200" size={40} />
                  ) : (
                    <XCircle
                      className="text-orange mx-2 sm:inline hidden"
                      size={40}
                    />
                  )}
                  <span className="mt-2 text-2xl text-grey text-center">
                    {passed
                      ? t({
                          id: isTrialJoinPage
                            ? 'trialClass.joinpage:systemCheck.checkSuccessful'
                            : 'trialClass.systemCheck.checkSuccessful',
                          message: isTrialJoinPage
                            ? "Computer check completed successfully! You're all set to attend the class — this modal will close in 5 seconds, or you can close it manually."
                            : "Computer check completed successfully! You're all set to attend the class.",
                        })
                      : t({
                          id: 'trialClass.systemCheck.checkFail',
                          message:
                            'Your computer is not compatible to attend the class. You must fix it before the class starts!',
                        })}
                  </span>
                </div>
              )}
              {!isChecking && !passed && (
                <div className="flex space-x-2 bg-red-50 border-l-4 border-red-200 text-grey px-5 py-2 font-600 text-lg">
                  <p className="my-2" style={{ minWidth: '12rem' }}>
                    <Trans id="trialClass.systemCheck.fixIssues">
                      Please fix these issues:
                    </Trans>
                  </p>
                  <div className="flex flex-wrap">
                    {errorList.map(({ message, icon }) => (
                      <div className="flex items-center m-2" key={message.id}>
                        <Icon
                          name={icon}
                          className="text-orange mr-2 sm:inline hidden"
                          size={18}
                        />
                        <p>
                          <MsgTrans msg={message} />
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!isChecking &&
                isCamOrMicCheckFailed &&
                VALID_BROWSER_KEYS.includes(browserKey) && (
                  <>
                    <div className="flex flex-col space-y-3 border border-grey-500 rounded-lg p-8">
                      <h2 className="text-xl text-grey font-600">
                        <Trans id="trialClass.systemCheck.videoHeading">
                          Watch video on how to fix the issue
                        </Trans>
                      </h2>
                      <video
                        src={CAM_AND_MIC_ACCESS_VIDEOS[browserKey]}
                        controls
                        className="w-full"
                        title="Give camera and mic access"
                      />
                    </div>
                    <div className="flex flex-col space-y-6 border border-grey-500 rounded-lg p-8">
                      <h2 className="text-xl text-grey font-600">
                        <Trans id="trialClass.systemCheck.fixIssueHeading">
                          Read how to fix the issue
                        </Trans>
                      </h2>
                      <div className="flex space-x-8">
                        {CAM_AND_MIC_ACCESS_STEPS[browserKey].map(
                          (step: JSX.Element, index: number) => (
                            <div
                              key={index}
                              className="flex flex-col w-1/3 space-y-5"
                            >
                              <LazyImage
                                path={`${IMAGE_PATH}browser-mic-cam-steps/`}
                                name={`${browserKey}-step-${index + 1}`}
                                alt={`Step ${index + 1}`}
                              />
                              <p className="text-lg leading-tight">
                                <span className="font-500">
                                  <Trans id="trialClass.systemCheck.stepText">
                                    Step {i18n.number(index + 1)}:
                                  </Trans>
                                </span>{' '}
                                {step}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </>
                )}
              <div className="grid grid-cols-3 gap-6">
                <div className="flex flex-col bg-grey-300 rounded-md items-center p-6">
                  <LazyImage
                    path={IMAGE_PATH}
                    name="speed"
                    alt="measuring internet speed"
                    className="w-24 my-8"
                  />
                  <div className="flex flex-col h-full justify-between text-lg leading-tight">
                    <p className="text-grey">
                      <Trans id="trialClass.systemCheck.minimumSpeed">
                        Make sure that you have an internet speed of atleast 5
                        Mbps.
                      </Trans>
                    </p>
                    <a
                      onClick={() => {
                        window.open('https://fast.com', '_blank')
                        // track download chrome cta click in segment on /trial-class/join/ page
                        Router.asPath.split('?')[0] === '/trial-class/join/' &&
                          window.globalThis?.analytics?.track(
                            TRACK_TEST_INTERNET_SPEED_CTA_CLICK,
                            segmentTrackingData
                          )
                      }}
                      className="text-orange hover:underline font-600 py-2 cursor-pointer"
                    >
                      <Trans id="trialClass.systemCheck.testSpeed">
                        Test internet speed
                      </Trans>
                    </a>
                  </div>
                </div>
                <div className="flex flex-col bg-grey-300 rounded-md items-center p-6">
                  <LazyImage
                    path={IMAGE_PATH}
                    name="browser"
                    alt="chrome logo"
                    className="w-24 my-8"
                  />
                  <div className="flex flex-col h-full justify-between text-lg leading-tight">
                    <p className="text-grey">
                      <Trans id="trialClass.systemCheck.latestChrome">
                        Download the latest version of Chrome for best
                        experience
                      </Trans>
                    </p>
                    <button
                      onClick={() => {
                        window.open('https://www.google.com/chrome/', '_blank')
                        // track download chrome cta click in segment on /trial-class/join/ page
                        Router.asPath.split('?')[0] === '/trial-class/join/' &&
                          window.globalThis?.analytics?.track(
                            TRACK_DOWNLOAD_GOOGLE_CHROME_CTA_CLICK,
                            segmentTrackingData
                          )
                      }}
                      className="text-orange hover:underline font-600 py-2"
                    >
                      <Trans id="trialClass.systemCheck.downloadChrome">
                        Download Chrome
                      </Trans>
                    </button>
                  </div>
                </div>
                <div className="flex flex-col bg-grey-300 rounded-md items-center p-6">
                  <LazyImage
                    path={IMAGE_PATH}
                    name="support"
                    alt="chrome logo"
                    className="w-24 my-8"
                  />
                  <div className="flex flex-col text-lg text-grey">
                    <Trans id="trialClass.systemCheck.contactUs">
                      <p>If you need help, call us on</p>
                      <a
                        href={
                          isIndia
                            ? `tel:${SUPPORT_PHONE_NUMBER.value}`
                            : `mailto:${SUPPORT_EMAIL.value}`
                        }
                        className="flex space-x-2 font-600 hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon
                          name={isIndia ? 'Phone' : 'Mail'}
                          className="w-5"
                        />
                        <span>
                          {isIndia
                            ? SUPPORT_PHONE_NUMBER.label
                            : SUPPORT_EMAIL.label}
                        </span>
                      </a>
                      <p className="self-center">Or</p>
                      <a
                        href={`https://wa.me/${WHATSAPP_SUPPORT_NUMBER.value}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn text-white bg-green-200 hover:bg-green-100 flex justify-center mt-1 text-center"
                      >
                        Message on WhatsApp
                      </a>
                    </Trans>
                  </div>
                </div>
              </div>
            </div>
            {autoClose && !isChecking && !passed && (
              <>
                <button
                  className="flex justify-center py-3 px-10 self-start bg-orange text-white rounded font-600 mt-5"
                  type="button"
                  onClick={() => Router.reload()}
                >
                  <Trans id="trialClass.systemCheck.allIssuesFixedBtnText">
                    Ok, I have fixed all the issues
                  </Trans>
                </button>
              </>
            )}
          </div>
        )}
      </>
    </Modal>
  )
}

export default React.memo(SystemCheckModal)
